/**
 * Per-customer AI spend limits.
 *
 * Lives inside the "Monthly spend limit" card in AI Settings, under the
 * platform-wide ceiling, because the two are one decision: what may be spent,
 * and by whom.
 *
 * DESIGN NOTES
 * ------------
 * * **Quiet inputs.** Twenty borderless fields that only outline on hover/focus
 *   read as a table you can edit, rather than a form with twenty things
 *   demanding attention.
 * * **Save on blur or Enter.** A Save button per row would mean forty controls
 *   on screen. Saving when you leave the field is what a spreadsheet does, and
 *   this is a spreadsheet-shaped task.
 * * **Activity first, everything else behind a toggle.** Most customers spend
 *   nothing. Showing all twenty by default buries the two that matter.
 * * **The bar means something even with no limit set.** Without a ceiling it
 *   shows share-of-largest-spender, so the table is readable on day one instead
 *   of twenty empty tracks.
 */
import { useMemo, useState } from "react";

import api from "../api/client";

export type CustomerBudgetRow = {
  customer_id: number;
  customer_name: string;
  is_demo: boolean;
  budget_usd: number;
  /** Measured, attributed spend. This is what the ceiling is enforced against. */
  spent_usd: number;
  /** Apportioned share of spend recorded before metering could attribute it. */
  estimated_usd: number;
  percent_used: number;
  enforced: boolean;
  automatic_paused: boolean;
  warning: boolean;
};

export type BudgetStatus = {
  budget_usd: number;
  spent_usd: number;
  percent_used: number;
  warn_percent: number;
  enforced: boolean;
  automatic_paused: boolean;
  customers: CustomerBudgetRow[];
  unattributed_usd: number;
  customer_budget_total_usd: number;
  customers_paused: number;
};

const money = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** A column heading that sorts. The caret only renders on the active column, so
 *  the current sort is unambiguous rather than three carets competing. */
function SortHeading({
  label,
  active,
  asc,
  onClick,
}: {
  label: string;
  active: boolean;
  asc: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      // aria-sort belongs on the column header cell, not on a button — this
      // is a div-based grid, so the state is announced via the label instead.
      aria-label={
        active
          ? `${label}, sorted ${asc ? "ascending" : "descending"}, activate to reverse`
          : `${label}, activate to sort`
      }
      className={`group inline-flex items-center gap-1 uppercase tracking-wide transition-colors ${
        active
          ? "text-gray-600 dark:text-gray-300 font-medium"
          : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
      }`}
    >
      {label}
      <span
        className={
          active ? "opacity-100" : "opacity-0 group-hover:opacity-40 transition-opacity"
        }
        aria-hidden="true"
      >
        {active && !asc ? "▾" : "▴"}
      </span>
    </button>
  );
}

function StatusPill({ row }: { row: CustomerBudgetRow }) {
  if (row.automatic_paused) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 dark:bg-red-500/10 px-2 py-0.5 text-[11px] font-medium text-red-700 dark:text-red-400">
        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
        Automation paused
      </span>
    );
  }
  if (row.warning) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        Near limit
      </span>
    );
  }
  if (row.enforced) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-400">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Within limit
      </span>
    );
  }
  return null;
}

export default function AICustomerBudgets({
  budget,
  onChange,
  onError,
}: {
  budget: BudgetStatus;
  onChange: (next: BudgetStatus) => void;
  onError: (msg: string | null) => void;
}) {
  // `budget.customers || []` inline hands the useMemo below a brand-new array
  // identity on every render whenever the field is absent, so the sort +
  // filter re-ran continuously.
  const rows = useMemo(() => budget.customers || [], [budget.customers]);
  const [showAll, setShowAll] = useState(false);
  const [filter, setFilter] = useState("");
  // Per-row transient state, keyed by customer id. `drafts` holds what the user
  // has typed but not yet committed, so a re-render from a sibling save can't
  // clobber the field they're editing.
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [savedId, setSavedId] = useState<number | null>(null);
  // Default to spend, descending: the reason to open this page is usually "who
  // is burning the budget", and A–Z buries that behind whoever starts with 'A'.
  // Name sorting is what you want once you're looking for a SPECIFIC customer,
  // which is the other half of the job.
  const [sortKey, setSortKey] = useState<"name" | "spend" | "limit">("spend");
  const [sortAsc, setSortAsc] = useState(false);

  function toggleSort(key: "name" | "spend" | "limit") {
    if (sortKey === key) {
      setSortAsc((v) => !v);
    } else {
      setSortKey(key);
      // Names read naturally A–Z; money reads naturally biggest-first.
      setSortAsc(key === "name");
    }
  }

  const active = rows.filter(
    (r) => r.spent_usd > 0 || r.estimated_usd > 0 || r.budget_usd > 0,
  );
  const hiddenCount = rows.length - active.length;

  const visible = useMemo(() => {
    const base = showAll ? rows : active;
    const q = filter.trim().toLowerCase();
    const filtered = q
      ? base.filter((r) => r.customer_name.toLowerCase().includes(q))
      : base;
    const dir = sortAsc ? 1 : -1;
    return [...filtered].sort((a, bb) => {
      if (sortKey === "name") {
        // localeCompare so "Ångström" and "apple" land where a human expects,
        // and numeric so "Site 2" sorts before "Site 10".
        return (
          dir *
          a.customer_name.localeCompare(bb.customer_name, undefined, {
            sensitivity: "base",
            numeric: true,
          })
        );
      }
      if (sortKey === "limit") {
        return dir * (a.budget_usd - bb.budget_usd) ||
          a.customer_name.localeCompare(bb.customer_name);
      }
      // Spend includes the estimate, so ordering stays meaningful before
      // metering has filled in real numbers.
      return (
        dir *
          (a.spent_usd + a.estimated_usd - (bb.spent_usd + bb.estimated_usd)) ||
        a.customer_name.localeCompare(bb.customer_name)
      );
    });
  }, [rows, active, showAll, filter, sortKey, sortAsc]);

  // Scale for the no-limit case: share of the biggest spender, so an unlimited
  // row still shows relative size instead of an empty track.
  const topSpend = Math.max(
    ...rows.map((r) => r.spent_usd + r.estimated_usd),
    0.01,
  );

  async function save(row: CustomerBudgetRow, raw: string) {
    const parsed = parseFloat(raw);
    const next = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    if (next === row.budget_usd) {
      // Nothing changed — don't fire a write (or an audit entry) for a blur.
      setDrafts((d) => {
        const { [row.customer_id]: _drop, ...rest } = d;
        return rest;
      });
      return;
    }
    setSavingId(row.customer_id);
    try {
      const r = await api.put(`/api/v1/ai/budget/customers/${row.customer_id}`, {
        monthly_budget_usd: next,
      });
      onChange(r.data);
      onError(null);
      setDrafts((d) => {
        const { [row.customer_id]: _drop, ...rest } = d;
        return rest;
      });
      setSavedId(row.customer_id);
      window.setTimeout(
        () => setSavedId((cur) => (cur === row.customer_id ? null : cur)),
        1600,
      );
    } catch (e: any) {
      onError(
        e.response?.data?.detail ||
          `Couldn't save the limit for ${row.customer_name}`,
      );
    } finally {
      setSavingId(null);
    }
  }

  const overCommitted =
    budget.enforced && budget.customer_budget_total_usd > budget.budget_usd;
  const anyEstimated = visible.some((r) => r.estimated_usd > r.spent_usd);

  return (
    <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-2xl">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Per-customer limits
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Give a customer its own monthly ceiling so one noisy tenant can't consume the
            whole budget and pause automation for everyone else. Both limits apply and the
            tighter one takes effect first. Leave a field blank for no customer limit.
          </p>
        </div>
        {rows.length > 8 && (
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Find a customer…"
            className="w-48 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
          />
        )}
      </div>

      {/* Column headings double as sort controls. Light styling, because the
          rows are self-describing — but they need to look clickable, or nobody
          discovers that sorting exists. */}
      <div className="mt-4 grid grid-cols-[minmax(0,1fr)_auto] gap-4 px-2 pb-1 text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
        <span className="flex items-center gap-3">
          <SortHeading
            label="Customer"
            active={sortKey === "name"}
            asc={sortAsc}
            onClick={() => toggleSort("name")}
          />
          <span className="text-gray-300 dark:text-gray-600">·</span>
          <SortHeading
            label="Spend this month"
            active={sortKey === "spend"}
            asc={sortAsc}
            onClick={() => toggleSort("spend")}
          />
        </span>
        <span className="text-right pr-1">
          <SortHeading
            label="Monthly limit"
            active={sortKey === "limit"}
            asc={sortAsc}
            onClick={() => toggleSort("limit")}
          />
        </span>
      </div>

      <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
        {visible.map((row) => {
          const total = row.spent_usd + row.estimated_usd;
          const pct = row.enforced
            ? Math.min(100, row.percent_used)
            : Math.min(100, (total / topSpend) * 100);
          const barColor = row.automatic_paused
            ? "bg-red-500"
            : row.warning
              ? "bg-amber-500"
              : row.enforced
                ? "bg-emerald-500"
                : "bg-gray-300 dark:bg-gray-600";
          const draft = drafts[row.customer_id];
          const value =
            draft !== undefined ? draft : row.budget_usd > 0 ? String(row.budget_usd) : "";
          // Mark the figure as approximate only when the estimate is actually
          // carrying it, not when a rounding-level amount is mixed in.
          const estimateDominates = row.estimated_usd > row.spent_usd;

          return (
            <div
              key={row.customer_id}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 px-2 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {row.customer_name}
                  </span>
                  {row.is_demo && (
                    <span className="rounded bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Demo
                    </span>
                  )}
                  <StatusPill row={row} />
                </div>
                <div className="mt-1.5 flex items-center gap-3">
                  <div className="h-1.5 flex-1 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${barColor} transition-all`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  {/* A leading "~" marks an estimate-derived figure. The full
                      explanation lives once under the table — repeating the
                      same sentence on twenty rows is noise, not clarity, and it
                      doubled the height of the whole section. */}
                  <span
                    className="w-24 text-right text-xs tabular-nums text-gray-600 dark:text-gray-400 shrink-0"
                    title={
                      estimateDominates
                        ? `Includes ~$${money(row.estimated_usd)} estimated from usage recorded before per-customer metering began. Limits are enforced against measured spend only.`
                        : undefined
                    }
                  >
                    {estimateDominates && (
                      <span className="text-gray-400 dark:text-gray-500">~</span>
                    )}
                    ${money(total)}
                    {row.enforced && (
                      <span className="text-gray-400 dark:text-gray-500">
                        {" / "}${money(row.budget_usd)}
                      </span>
                    )}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 justify-end pl-4">
                <div className="relative">
                  <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 dark:text-gray-500">
                    $
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={10}
                    value={value}
                    disabled={savingId === row.customer_id}
                    onChange={(e) =>
                      setDrafts((d) => ({ ...d, [row.customer_id]: e.target.value }))
                    }
                    onBlur={(e) => save(row, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                      if (e.key === "Escape") {
                        setDrafts((d) => {
                          const { [row.customer_id]: _drop, ...rest } = d;
                          return rest;
                        });
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    placeholder="Set limit"
                    aria-label={`Monthly AI limit for ${row.customer_name}`}
                    /* Dashed at rest so it reads as an editable field rather
                       than static text, solid on hover/focus. A fully
                       borderless input here looked like a label nobody could
                       tell was clickable. */
                    className="w-28 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 hover:border-solid hover:border-gray-400 dark:hover:border-gray-500 hover:bg-white dark:hover:bg-gray-900/60 focus:border-solid focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-900 bg-transparent dark:text-gray-100 pl-5 pr-2 py-1 text-sm text-right tabular-nums outline-none transition-all disabled:opacity-50 placeholder:text-gray-400 placeholder:text-xs placeholder:font-normal"
                  />
                </div>
                {/* Fixed-width slot so a row doesn't shift when the tick shows. */}
                <span className="w-4 text-xs">
                  {savingId === row.customer_id && (
                    <span className="text-gray-400">…</span>
                  )}
                  {savedId === row.customer_id && (
                    <span className="text-emerald-500" aria-label="Saved">
                      ✓
                    </span>
                  )}
                </span>
              </div>
            </div>
          );
        })}

        {visible.length === 0 && (
          <p className="px-2 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            {filter.trim()
              ? "No customer matches that name."
              : "No AI spend recorded against a customer yet."}
          </p>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between gap-4 flex-wrap text-xs">
        <div className="text-gray-500 dark:text-gray-400 space-y-1">
          {/* The estimate caveat, stated ONCE for the table instead of on every
              row. Rows carry a "~" and the detail in a tooltip. */}
          {anyEstimated && (
            <p>
              Amounts marked <span className="text-gray-400">~</span> are estimated: spend
              recorded before per-customer metering began, apportioned by each customer's
              share of AI calls. Limits are enforced against measured spend only.
            </p>
          )}
          {budget.unattributed_usd > 0 && (
            <p title="Cross-tenant AI work — fleet-wide digests and multi-customer sweeps — plus anything recorded before per-customer metering began.">
              ${money(budget.unattributed_usd)} this month isn't tied to a single customer.
            </p>
          )}
          {budget.customer_budget_total_usd > 0 && (
            <p>
              Per-customer limits total ${money(budget.customer_budget_total_usd)}.
            </p>
          )}
        </div>
        {hiddenCount > 0 && !filter.trim() && (
          <button
            onClick={() => setShowAll((v) => !v)}
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            {showAll
              ? "Show only customers with activity"
              : `Show all ${rows.length} customers`}
          </button>
        )}
      </div>

      {/* Worth saying out loud: the platform cap still wins, so a set of
          per-customer limits adding up to more than it will not all be
          honoured. Silently letting the numbers disagree would make the page
          look like it was lying later. */}
      {overCommitted && (
        <p className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
          Per-customer limits add up to ${money(budget.customer_budget_total_usd)}, more than
          the ${money(budget.budget_usd)} platform limit. The platform limit still applies
          first, so automation can pause even while a customer is under its own limit.
        </p>
      )}
    </div>
  );
}
